import * as THREE from 'three';
import { PLAYER_COLORS } from '../../../utils/constants.js';
import { colored } from '../../Style.js';
import { SCIFI, glowColor, glowMaterial, bake, prism, hexColor } from './units-style.js';

const NEUTRAL_RING = 0x9aa3ad;

const WALL_BASE = 0.6; // Top of the plinth
const WALL_TOP = 2.6;
const HALF = 2; // Half-depth of the bay
const SLOPE = 0.3; // How far the side walls lean in

// A clone bay: a sloped-wall bunker on a landing pad, with a glowing
// fabrication door, team armour panels, roof antennas and a holo sign in the
// owner's colour. Unclaimed bays are white and grey.
export class Barracks {
  constructor(scene, barracks) {
    this.scene = scene;
    this.barracks = barracks;
    this.meshGroup = new THREE.Group();
    this.buildingMesh = null;
    this.roofMesh = null;
    this.flagMesh = null; // Holo sign
    this.bannerMesh = null; // Team glow trim
    this.lanternMesh = null; // Glowing door
    this.rangeMesh = null;
    this.healthBarGroup = null;
    this.healingIndicator = null;
    this.wasDestroyed = false;
    this.create();
  }

  create() {
    const neutral = this.barracks.ownerId === -1;

    // Per-bay materials so a destroyed one can darken and fade alone
    this.structureMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      vertexColors: true,
      flatShading: true,
      metalness: 0.35,
      roughness: 0.5
    });
    this.teamMaterial = new THREE.MeshStandardMaterial({
      color: this.getPanelColor(),
      roughness: 0.5,
      metalness: 0.3,
      flatShading: true,
      emissive: this.getColor(),
      emissiveIntensity: neutral ? 0 : 0.25
    });
    this.glowMaterial = glowMaterial({ color: this.getGlowColor() });
    this.lanternMaterial = glowMaterial({ color: this.getGlowColor() });

    // Landing pad and plinth
    const footing = new THREE.Mesh(bake(this.buildFooting()), this.structureMaterial);
    footing.castShadow = true;
    footing.receiveShadow = true;
    this.meshGroup.add(footing);

    // Sloped-wall bunker with its team panels as a child, so they lean together
    this.buildingMesh = new THREE.Mesh(bake(this.buildWalls()), this.structureMaterial);
    this.buildingMesh.castShadow = true;
    this.buildingMesh.receiveShadow = true;
    this.meshGroup.add(this.buildingMesh);
    this.panelMesh = new THREE.Mesh(bake(this.buildPanels()), this.teamMaterial);
    this.panelMesh.castShadow = true;
    this.buildingMesh.add(this.panelMesh);

    // Roof deck, comms module and antennas, with blinking antenna tips
    this.roofMesh = new THREE.Mesh(bake(this.buildRoof()), this.structureMaterial);
    this.roofMesh.castShadow = true;
    this.roofMesh.receiveShadow = true;
    this.meshGroup.add(this.roofMesh);
    this.beacons = new THREE.Mesh(bake([
      colored(new THREE.BoxGeometry(0.12, 0.12, 0.12), 0xffffff, { x: 1.35, y: 4.55, z: -1.3 }),
      colored(new THREE.BoxGeometry(0.1, 0.1, 0.1), 0xffffff, { x: -1.45, y: 4.05, z: -1.15 })
    ]), this.glowMaterial);
    this.roofMesh.add(this.beacons);

    // Fabrication door glowing between dark vent slats
    this.lanternMesh = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 1.55), this.lanternMaterial);
    this.lanternMesh.position.set(0, WALL_BASE + 0.82, HALF + 0.07);
    this.meshGroup.add(this.lanternMesh);

    // Glowing trim: door frame, side window slits, pad corner markers
    this.bannerMesh = new THREE.Mesh(bake(this.buildTrim()), this.glowMaterial);
    this.meshGroup.add(this.bannerMesh);

    // Holo sign floating above the door (shows ownership)
    this.signTexture = this.createSignTexture();
    this.signMaterial = new THREE.MeshBasicMaterial({
      map: this.signTexture,
      color: this.getGlowColor(),
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    this.signMaterial.toneMapped = false;
    this.flagMesh = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 0.6), this.signMaterial);
    this.flagMesh.position.set(0, 3.45, HALF - 0.05);
    this.flagMesh.rotation.x = -0.2;
    this.meshGroup.add(this.flagMesh);

    // Range indicator (shows claim radius)
    const rangeGeometry = new THREE.RingGeometry(7.5, 8, 32);
    const rangeMaterial = new THREE.MeshBasicMaterial({
      color: this.getRingColor(),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.25
    });
    this.rangeMesh = new THREE.Mesh(rangeGeometry, rangeMaterial);
    this.rangeMesh.rotation.x = -Math.PI / 2;
    this.rangeMesh.position.y = 0.05;
    this.meshGroup.add(this.rangeMesh);

    // Health bar background
    this.healthBarGroup = new THREE.Group();
    this.healthBarGroup.position.y = 5.5;

    const healthBarBg = new THREE.Mesh(
      new THREE.BoxGeometry(4, 0.3, 0.1),
      new THREE.MeshBasicMaterial({ color: 0x333333 })
    );
    this.healthBarGroup.add(healthBarBg);

    // Health bar fill
    this.healthBarFill = new THREE.Mesh(
      new THREE.BoxGeometry(4, 0.3, 0.12),
      new THREE.MeshBasicMaterial({ color: 0x00ff00 })
    );
    this.healthBarFill.position.z = 0.01;
    this.healthBarGroup.add(this.healthBarFill);

    this.meshGroup.add(this.healthBarGroup);

    // Healing indicator (glowing cross icon) - hidden by default
    this.healingIndicator = new THREE.Group();
    this.healingIndicator.position.y = 6;
    this.healingIndicator.visible = false;

    // Cross shape using two boxes
    const crossMaterial = new THREE.MeshBasicMaterial({
      color: 0x5cffa8,
      transparent: true,
      opacity: 0.8
    });
    const crossVertical = new THREE.Mesh(
      new THREE.BoxGeometry(0.3, 1.2, 0.1),
      crossMaterial
    );
    const crossHorizontal = new THREE.Mesh(
      new THREE.BoxGeometry(1.2, 0.3, 0.1),
      crossMaterial
    );
    this.healingIndicator.add(crossVertical);
    this.healingIndicator.add(crossHorizontal);

    // Outer glow ring
    const glowGeometry = new THREE.RingGeometry(0.8, 1, 6);
    const haloMaterial = new THREE.MeshBasicMaterial({
      color: 0x5cffa8,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });
    this.healingGlow = new THREE.Mesh(glowGeometry, haloMaterial);
    this.healingIndicator.add(this.healingGlow);

    this.meshGroup.add(this.healingIndicator);

    // Position the group
    this.meshGroup.position.set(
      this.barracks.position.x,
      this.barracks.position.y,
      this.barracks.position.z
    );

    this.scene.add(this.meshGroup);
  }

  buildFooting() {
    const parts = [];

    // Gunmetal pad under a grid of alloy deck plates, gaps reading as panel lines
    parts.push(colored(new THREE.BoxGeometry(6, 0.16, 6), SCIFI.gunmetal, { y: 0.08 }));
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        parts.push(colored(new THREE.BoxGeometry(1.38, 0.08, 1.38), (i + j) % 2 ? SCIFI.alloyDark : 0x7a838f, {
          x: -2.25 + i * 1.5, y: 0.2, z: -2.25 + j * 1.5
        }));
      }
    }

    // Plinth the bunker sits on, with a ramp up to the door
    parts.push(colored(new THREE.BoxGeometry(4.7, 0.4, 4.4), SCIFI.alloyDark, { y: 0.4 }));
    parts.push(colored(new THREE.BoxGeometry(4.8, 0.06, 4.5), SCIFI.gunmetal, { y: 0.6 }));
    parts.push(colored(prism([[HALF + 0.2, 0.6], [HALF + 0.95, 0.2], [HALF + 0.95, 0.16], [HALF + 0.2, 0.16]], 1.6), SCIFI.alloy));

    return parts;
  }

  buildWalls() {
    const parts = [];
    const base = HALF + 0.2;

    // Sloped side walls: the profile's first axis becomes X once turned
    parts.push(colored(prism([[-base, WALL_BASE], [base, WALL_BASE], [base - SLOPE, WALL_TOP], [-base + SLOPE, WALL_TOP]], HALF * 2), SCIFI.white, {
      ry: Math.PI / 2
    }));
    // Gunmetal corner frames and a mid-height seam
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        parts.push(colored(new THREE.BoxGeometry(0.22, WALL_TOP - WALL_BASE + 0.05, 0.22), SCIFI.gunmetal, {
          x: sx * (base - SLOPE / 2 - 0.02), y: (WALL_BASE + WALL_TOP) / 2, z: sz * (HALF + 0.02), rz: sx * Math.atan2(SLOPE, WALL_TOP - WALL_BASE)
        }));
      }
    }
    for (const sz of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(base * 2 - 0.25, 0.05, 0.04), SCIFI.black, { y: 1.62, z: sz * (HALF + 0.01) }));
    }

    // Door recess with vent slats across the glow
    parts.push(colored(new THREE.BoxGeometry(1.6, 1.85, 0.12), SCIFI.gunmetal, { y: WALL_BASE + 0.92, z: HALF + 0.0 }));
    for (let i = 0; i < 4; i++) {
      parts.push(colored(new THREE.BoxGeometry(1.12, 0.08, 0.06), SCIFI.gunmetal, { y: WALL_BASE + 0.3 + i * 0.38, z: HALF + 0.09 }));
    }
    parts.push(colored(new THREE.BoxGeometry(1.8, 0.24, 0.3), SCIFI.alloy, { y: WALL_BASE + 1.94, z: HALF + 0.08 }));

    // Vent grilles on the back wall
    for (const sx of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.9, 0.6, 0.08), SCIFI.gunmetal, { x: sx * 0.8, y: 1.6, z: -HALF - 0.03 }));
    }
    return parts;
  }

  // Large team panels on the sloped sides and either side of the door
  buildPanels() {
    const lean = Math.atan2(SLOPE, WALL_TOP - WALL_BASE);
    const parts = [];
    for (const sx of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.08, 1.3, 3.0), 0xffffff, {
        x: sx * (HALF + 0.2 - SLOPE * 0.48 + 0.04), y: 1.55, rz: sx * lean
      }));
      parts.push(colored(new THREE.BoxGeometry(0.5, 1.5, 0.08), 0xffffff, { x: sx * 1.3, y: 1.5, z: HALF + 0.04 }));
    }
    return parts;
  }

  buildRoof() {
    const top = HALF + 0.2 - SLOPE;
    return [
      colored(new THREE.BoxGeometry(top * 2 + 0.2, 0.2, HALF * 2 + 0.2), SCIFI.gunmetal, { y: WALL_TOP + 0.1 }),
      // Comms module and dish
      colored(new THREE.BoxGeometry(2.0, 0.55, 1.8), SCIFI.alloy, { y: WALL_TOP + 0.47, z: -0.5 }),
      colored(new THREE.BoxGeometry(2.04, 0.06, 1.84), SCIFI.alloyDark, { y: WALL_TOP + 0.6, z: -0.5 }),
      colored(new THREE.CylinderGeometry(0.06, 0.1, 0.4, 5), SCIFI.gunmetal, { x: -0.4, y: WALL_TOP + 0.95, z: -0.6 }),
      colored(new THREE.SphereGeometry(0.42, 8, 3, 0, Math.PI * 2, 0, Math.PI / 2), SCIFI.white, { x: -0.4, y: WALL_TOP + 1.25, z: -0.6, rx: Math.PI - 0.6 }),
      // Antennas
      colored(new THREE.CylinderGeometry(0.04, 0.06, 1.8, 4), SCIFI.alloy, { x: 1.35, y: WALL_TOP + 1.05, z: -1.3 }),
      colored(new THREE.BoxGeometry(0.3, 0.04, 0.04), SCIFI.alloy, { x: 1.35, y: WALL_TOP + 1.5, z: -1.3 }),
      colored(new THREE.CylinderGeometry(0.03, 0.05, 1.3, 4), SCIFI.alloy, { x: -1.45, y: WALL_TOP + 0.8, z: -1.15 }),
      // Emitter bar for the holo sign
      colored(new THREE.BoxGeometry(2.0, 0.14, 0.2), SCIFI.alloyDark, { y: WALL_TOP + 0.27, z: HALF - 0.05 })
    ];
  }

  buildTrim() {
    const parts = [
      // Door frame
      colored(new THREE.BoxGeometry(1.4, 0.07, 0.05), 0xffffff, { y: WALL_BASE + 1.7, z: HALF + 0.08 }),
      colored(new THREE.BoxGeometry(0.07, 1.7, 0.05), 0xffffff, { x: -0.7, y: WALL_BASE + 0.85, z: HALF + 0.08 }),
      colored(new THREE.BoxGeometry(0.07, 1.7, 0.05), 0xffffff, { x: 0.7, y: WALL_BASE + 0.85, z: HALF + 0.08 }),
      // Ramp edge lights and the sign emitter
      colored(new THREE.BoxGeometry(1.5, 0.04, 0.05), 0xffffff, { y: 0.2, z: HALF + 0.93 }),
      colored(new THREE.BoxGeometry(1.8, 0.04, 0.05), 0xffffff, { y: WALL_TOP + 0.33, z: HALF + 0.06 })
    ];
    // Window slits along the sloped sides
    const lean = Math.atan2(SLOPE, WALL_TOP - WALL_BASE);
    for (const sx of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.05, 0.08, 3.2), 0xffffff, {
        x: sx * (HALF + 0.2 - SLOPE * 0.88 + 0.04), y: 2.36, rz: sx * lean
      }));
    }
    // L-shaped corner markers on the pad
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        parts.push(colored(new THREE.BoxGeometry(0.8, 0.03, 0.1), 0xffffff, { x: sx * 2.55, y: 0.25, z: sz * 2.9 }));
        parts.push(colored(new THREE.BoxGeometry(0.1, 0.03, 0.8), 0xffffff, { x: sx * 2.9, y: 0.25, z: sz * 2.55 }));
      }
    }
    return parts;
  }

  // White sign artwork, tinted by the material colour so it follows the owner
  createSignTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 4;
    ctx.strokeRect(4, 4, canvas.width - 8, canvas.height - 8);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.fillRect(4, 4, canvas.width - 8, canvas.height - 8);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 34px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('CLONE BAY', canvas.width / 2, canvas.height / 2 + 2);
    for (const x of [22, canvas.width - 22]) {
      ctx.beginPath();
      ctx.moveTo(x, 18);
      ctx.lineTo(x + 10, 32);
      ctx.lineTo(x, 46);
      ctx.lineTo(x - 10, 32);
      ctx.closePath();
      ctx.fill();
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  getColor() {
    if (this.barracks.ownerId === -1) {
      return 0x888888; // Gray for neutral
    }
    return hexColor(PLAYER_COLORS[this.barracks.ownerId]);
  }

  getPanelColor() {
    return this.barracks.ownerId === -1 ? SCIFI.neutralPanel : this.getColor();
  }

  getGlowColor() {
    return this.barracks.ownerId === -1 ? new THREE.Color(SCIFI.neutralGlow) : glowColor(this.getColor());
  }

  getRingColor() {
    return this.barracks.ownerId === -1 ? NEUTRAL_RING : this.getColor();
  }

  update(barracksData, time) {
    const ownerChanged = this.barracks.ownerId !== barracksData.ownerId;
    this.barracks = barracksData;
    const neutral = this.barracks.ownerId === -1;

    // Repaint panels and glows if the owner changed
    if (ownerChanged) {
      const glowCol = this.getGlowColor();
      this.teamMaterial.color.setHex(this.getPanelColor());
      this.teamMaterial.emissive.setHex(this.getColor());
      this.teamMaterial.emissiveIntensity = neutral ? 0 : 0.25;
      this.glowMaterial.color.copy(glowCol);
      this.lanternMaterial.color.copy(glowCol);
      this.signMaterial.color.copy(glowCol);
      if (this.rangeMesh) {
        this.rangeMesh.material.color.setHex(this.getRingColor());
      }
    }

    // Handle destroyed state
    const destroyed = !!this.barracks.isDestroyed;
    if (destroyed !== this.wasDestroyed) {
      this.wasDestroyed = destroyed;
      this.setDestroyedLook(destroyed);
    }

    if (destroyed) {
      // Show respawn timer through opacity
      this.structureMaterial.opacity = 0.3 + 0.2 * Math.sin(time * 4);
    } else {
      // Door hums, the sign flickers now and then, antenna tips blink
      const hum = 0.85 + Math.sin(time * 3) * 0.1;
      this.lanternMaterial.color.copy(this.getGlowColor()).multiplyScalar(hum);
      const glitch = Math.sin(time * 23) > 0.96 ? 0.35 : 0;
      this.signMaterial.opacity = 0.8 + Math.sin(time * 5) * 0.05 - glitch;
      this.flagMesh.position.y = 3.45 + Math.sin(time * 1.6) * 0.05;
      this.beacons.visible = Math.sin(time * 3) > -0.3;

      // Pulse range indicator for neutral barracks
      if (neutral && this.rangeMesh) {
        const pulse = 0.15 + Math.sin(time * 2) * 0.1;
        this.rangeMesh.material.opacity = pulse;
      } else if (this.rangeMesh) {
        this.rangeMesh.material.opacity = 0.25;
      }
    }

    // Update health bar
    this.updateHealthBar();

    // Update healing indicator based on occupant count
    this.updateHealingIndicator(time);
  }

  // Dark, tilted and unpowered while destroyed; restored on respawn.
  setDestroyedLook(destroyed) {
    this.structureMaterial.color.setHex(destroyed ? SCIFI.dead : 0xffffff);
    this.structureMaterial.transparent = destroyed;
    this.structureMaterial.opacity = 1.0;
    this.structureMaterial.depthWrite = !destroyed;
    this.structureMaterial.needsUpdate = true;
    this.buildingMesh.rotation.z = destroyed ? 0.08 : 0;
    this.roofMesh.visible = !destroyed;
    this.panelMesh.visible = !destroyed;
    this.flagMesh.visible = !destroyed;
    this.bannerMesh.visible = !destroyed;
    this.lanternMesh.visible = !destroyed;
    this.rangeMesh.material.opacity = destroyed ? 0.1 : 0.25;
  }

  updateHealingIndicator(time) {
    if (!this.healingIndicator) return;

    const hasOccupants = this.barracks.occupantCount > 0;
    this.healingIndicator.visible = hasOccupants && !this.barracks.isDestroyed;

    if (hasOccupants && !this.barracks.isDestroyed) {
      // Pulse the indicator
      const pulse = 0.6 + Math.sin(time * 4) * 0.4;
      this.healingIndicator.children.forEach(child => {
        if (child.material) {
          child.material.opacity = pulse;
        }
      });

      // Rotate the glow
      if (this.healingGlow) {
        this.healingGlow.rotation.z = time * 2;
      }

      // Scale pulse
      const scale = 1 + Math.sin(time * 3) * 0.1;
      this.healingIndicator.scale.set(scale, scale, scale);
    }
  }

  updateHealthBar() {
    if (!this.healthBarFill) return;

    const healthPercent = this.barracks.health / this.barracks.maxHealth;
    this.healthBarFill.scale.x = Math.max(0.01, healthPercent);
    this.healthBarFill.position.x = (healthPercent - 1) * 2; // Shift left as it decreases

    // Change color based on health
    if (healthPercent > 0.6) {
      this.healthBarFill.material.color.setHex(0x00ff00); // Green
    } else if (healthPercent > 0.3) {
      this.healthBarFill.material.color.setHex(0xffff00); // Yellow
    } else {
      this.healthBarFill.material.color.setHex(0xff0000); // Red
    }

    // Hide health bar when at full health
    this.healthBarGroup.visible = healthPercent < 1;
  }

  // Check if unit is in claiming range
  isInRange(position) {
    const dx = position.x - this.barracks.position.x;
    const dz = position.z - this.barracks.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.barracks.claimRadius * this.barracks.claimRadius;
  }

  // Check if barracks can be claimed by player
  canBeClaimed(playerId) {
    if (this.barracks.isDestroyed) return false;
    if (this.barracks.ownerId === playerId) return false;
    return true;
  }

  remove() {
    this.scene.remove(this.meshGroup);
    // Every material here belongs to this bay
    const materials = new Set();
    this.meshGroup.traverse(child => {
      if (!child.isMesh) return;
      child.geometry.dispose();
      materials.add(child.material);
    });
    materials.forEach(material => material.dispose());
    this.signTexture.dispose();
  }
}
